// Le Parcours en plein écran (D-161, D-162 ; maquettes P5 à P10) : la tablette au bord du terrain, recopiée sur la TV.
// Le plateau (un demi-terrain aux couleurs du club, les pions en maillots), une colonne par équipe (« Lancer le dé »,
// « Fait ! / Raté », les cases d'action), et les vrais moments de jeu : le dé qui roule avec un son, le pion qui avance case
// par case, la carte qui se retourne en grand 3 s puis se range chez l'équipe, l'arrivée fêtée. L'arbitrage du coach : un
// appui long de 1,5 s sur la colonne d'une équipe (ou « Arbitrer » en haut), sans code (D-174) ; le panneau se referme au
// bout de 30 s. Les règles sont dans src/domain/parcours.ts, le moteur commun dans
// src/domain/game.ts ; l'écran maintenu allumé, le plein écran et la parade iOS viennent du moteur des drills (D-098).
// Lot 2 (D-167) : les téléphones par QR code (game-live-run.ts) : un par équipe (« Lancer le dé », « Fait ! / Raté », les
// tirs), le coach en télécommande ; la base fait foi, la tablette applique leurs gestes dans l'ordre et publie la partie.
// D-174 : l'appareil de la partie (tablette ou téléphone) gère toutes les équipes ; pendant qu'un dé roule ou qu'une carte
// se retourne, « Fait ! », « Raté » ou le dé d'une autre équipe sont notés et joués juste après (comme ceux des téléphones).
import './game.css';
import { fmtGameTime, gameMs, paused, recentLog } from '../domain/game';
import { FAM_HEX, cardTags, cardTitle, type Card } from '../domain/game-cards';
import {
  ABOUT_MIN, ACTION_CASE_LABEL, ACTION_CASE_RULE, FAIL_LABEL, SANCTION_LABEL, STOP_MS, bilan, canRoll, declare, endGame, invalidate, jokerChange, lastActionText, lastDone,
  nameOfP, newParcours, participantsOf, pause, ranking, roll, runResult, sanction, shot, shotsOf, skipping, undo,
  type Fx, type ParcoursConfig, type ParcoursState, type Sanction, type Step,
} from '../domain/parcours';
import { agree, type TeamGender } from '../domain/labels';
import { noteGameRun } from '../lib/games-api';
import type { LiveAction } from '../lib/game-live-api';
import { parcoursView } from '../domain/game-live';
import { ICON, countdown, drawJoin, flash, openLive, type LiveCtl, type LiveTeamSeat } from './game-live-run';
import { engage } from './drill-engine';
import { ACT_GLYPH, PAWN_OFF, boardSvg, cardBack, cardHtml, caseRadius, casesOf, clubLogo, confetti, die, esc, famPicto, inSentence, jersey, miniCard, pawnInner, type Kit } from './game-art';
import { setGameSound, sndBonus, sndFanfare, sndFlip, sndKo, sndOk, sndRoll, sndStep } from './game-sounds';

export interface RunInput {
  cfg: ParcoursConfig;
  title: string;
  /** « U13 filles » : sous le titre. */
  teamLabel: string;
  gender: TeamGender;
  clubId: string;
  teamId: string | null;
  gameId: string | null;
  /** Prénom de chaque personne (affichage seulement). */
  names: Map<string, string>;
  /** « Garder ce jeu » : la partie préparée se range dans Drills et jeux ; rend son identifiant. */
  keep?: () => Promise<string | null>;
  onClose?: () => void;
}

const sleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms));
const IC = {
  pause: `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>`,
  play: `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4v16l13-8z"/></svg>`,
  check: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" width="28" height="28"><path d="M5 12l5 5L20 7"/></svg>`,
  ban: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M5.6 5.6l12.8 12.8"/></svg>`,
  flag: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 21V4M5 4h11l-2 4 2 4H5"/></svg>`,
  undo: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 14L4 9l5-5"/><path d="M4 9h10a6 6 0 0 1 0 12h-3"/></svg>`,
  stop: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>`,
  book: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2z"/><path d="M4 19V5M8 7h7"/></svg>`,
};
const SPARK = (x: number, y: number, s: number, c = '#FACC15') => `<svg class="spark" style="left:${x}px;top:${y}px;width:${s}px;height:${s}px" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 1l2.6 8.4L23 12l-8.4 2.6L12 23l-2.6-8.4L1 12l8.4-2.6z" fill="${c}"/></svg>`;
const mmss = (ms: number) => { const s = Math.round(ms / 1000); return `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, '0')}`; };
const ord = (k: number) => (k === 1 ? '1er' : `${k}e`);

// -----------------------------------------------------------------------------------------------------------------
// La partie
// -----------------------------------------------------------------------------------------------------------------
export function runParcours(inp: RunInput): void {
  const cfg = inp.cfg;
  setGameSound(cfg.sound);
  const reduce = (() => { try { return matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; } })();
  const T = (ms: number) => (reduce ? Math.round(ms / 3) : ms);
  const participants = participantsOf(cfg, id => inp.names.get(id) ?? '');
  const kitOf = new Map(participants.map(p => [p.id, { color: p.color, ink: p.ink, letter: p.letter } as Kit]));
  const n = cfg.length;
  const P = casesOf(n);
  const pawnScale = n <= 12 ? 1.15 : n <= 20 ? 1.05 : .9;
  const solo = cfg.mode === 'solo';
  const plural = !solo;
  let gameId = inp.gameId;
  let live: LiveCtl | null = null;
  /** L'heure de la partie : celle de la base quand des téléphones jouent (D-167), sinon celle de la tablette. */
  const clock = (): number => (live ? live.now() : Date.now());
  let started = !((cfg.phones || cfg.remote) && cfg.mode === 'teams' && inp.teamId);
  let seq = 0;
  const queue: LiveAction[] = [];
  let g: ParcoursState = newParcours(cfg, participants, Math.random, Date.now());
  let busy = false;
  let anim: { who: string; kind: 'rolling' | 'card' } | null = null;
  let coach: { who: string | null; until: number } | null = null;
  let askJoker: string | null = null;
  let noted = false;
  let stopArmed = false;
  /** Les équipes dont un geste attend la fin de l'animation en cours (noté, joué juste après). */
  const pend = new Set<string>();

  const host = document.createElement('div');
  host.className = 'gm-tv';
  host.setAttribute('role', 'dialog');
  host.setAttribute('aria-label', 'Le Parcours');
  document.body.append(host);
  const release = engage(host, 'Le Parcours');

  const name = (id: string | null) => (id ? nameOfP(g, id) : '');
  const membersLine = (id: string) => {
    const p = participants.find(x => x.id === id)!;
    return solo ? '' : p.members.map(m => inp.names.get(m)).filter(Boolean).join(' · ');
  };

  // --- Le cadre ---------------------------------------------------------------------------------------------------
  host.innerHTML = `<div class="gm-top"></div><div class="gm-main"><div class="gm-bwrap">${boardSvg({ n, actions: cfg.actions })}<div class="gm-over"></div></div><div class="gm-side n${participants.length}"></div></div>`;
  const top = host.querySelector<HTMLElement>('.gm-top')!;
  const main = host.querySelector<HTMLElement>('.gm-main')!;
  let side = host.querySelector<HTMLElement>('.gm-side')!;
  let bwrap = host.querySelector<HTMLElement>('.gm-bwrap')!;
  let over = host.querySelector<HTMLElement>('.gm-over')!;
  let svg = host.querySelector<SVGSVGElement>('.gm-board')!;

  // --- Les pions --------------------------------------------------------------------------------------------------
  const NS = 'http://www.w3.org/2000/svg';
  const pawnEls = new Map<string, SVGGElement>();
  const buildPawns = () => {
    const layer = svg.querySelector('.gm-pawns')!;
    layer.innerHTML = '';
    pawnEls.clear();
    for (const p of participants) {
      const el = document.createElementNS(NS, 'g') as SVGGElement;
      el.setAttribute('class', 'gm-pawn');
      el.innerHTML = `<g class="hop"><g transform="scale(${pawnScale})">${pawnInner(kitOf.get(p.id)!)}</g></g>`;
      layer.append(el);
      pawnEls.set(p.id, el);
    }
  };
  /** Où se pose le pion : la case, décalé si d'autres pions y sont (dans l'ordre des participants). */
  const spot = (id: string, at: number, ats: Map<string, number>) => {
    const same = participants.filter(p => ats.get(p.id) === at);
    const k = Math.max(0, same.findIndex(p => p.id === id));
    const off = PAWN_OFF[k] ?? [0, 0];
    const c = P[Math.max(0, Math.min(n, at))]!;
    return [c[0] + off[0], c[1] - 26 * pawnScale + off[1]] as const;
  };
  const shown = new Map<string, number>();
  const placeAll = (instant = false) => {
    for (const p of participants) shown.set(p.id, g.ps[p.id]!.at);
    for (const p of participants) place(p.id, shown.get(p.id)!, instant);
  };
  const place = (id: string, at: number, instant = false) => {
    shown.set(id, at);
    const el = pawnEls.get(id)!;
    const [x, y] = spot(id, at, shown);
    if (instant) el.style.transition = 'none';
    el.style.transform = `translate(${x}px, ${y}px)`;
    if (instant) { void el.getBoundingClientRect(); el.style.transition = ''; }
  };
  const hop = (id: string) => { const el = pawnEls.get(id)!; el.classList.remove('hopping'); void (el as unknown as HTMLElement).getBoundingClientRect(); el.classList.add('hopping'); };
  const ring = (at: number, color: string, ms = 2200) => {
    const hl = svg.querySelector('.gm-hl')!;
    const r = caseRadius(n);
    const c = P[at]!;
    const el = document.createElementNS(NS, 'g');
    el.innerHTML = `<circle cx="${c[0]}" cy="${c[1]}" r="${r + 12}" fill="none" stroke="${color}" stroke-width="6"/><circle class="gm-ring" cx="${c[0]}" cy="${c[1]}" r="${r + 22}" fill="none" stroke="${color}" stroke-width="3"/>`;
    hl.append(el);
    setTimeout(() => el.remove(), ms);
  };
  const trail = (id: string, from: number, to: number) => {
    const pts = P.slice(Math.min(from, to), Math.max(from, to) + 1);
    if (pts.length < 2) return;
    const d = 'M' + pts.map(p => p.join(' ')).join(' L');
    const el = document.createElementNS(NS, 'g');
    el.innerHTML = `<path d="${d}" fill="none" stroke="${kitOf.get(id)!.color}" stroke-width="10" stroke-linecap="round" stroke-dasharray="2 16" opacity=".95"/>`;
    svg.querySelector('.gm-trails')!.append(el);
    setTimeout(() => el.remove(), 3500);
  };
  const bubble = (at: number, text: string, ko: boolean) => {
    const c = P[Math.max(0, Math.min(n, at))]!;
    const w = Math.max(56, text.length * 13 + 24);
    const el = document.createElementNS(NS, 'g');
    el.setAttribute('transform', `translate(${c[0]} ${c[1] - 86})`);
    el.innerHTML = `<g class="gm-bubble"><rect x="${-w / 2}" y="-20" width="${w}" height="38" rx="19" fill="${ko ? '#F87171' : '#22C55E'}" stroke="#17191C" stroke-width="3"/>
      <text y="9" text-anchor="middle" font-family="Anton, Impact, sans-serif" font-size="22" fill="${ko ? '#1A0505' : '#06240F'}">${esc(text.toUpperCase())}</text></g>`;
    svg.querySelector('.gm-fx')!.append(el);
    setTimeout(() => el.remove(), 1700);
  };

  // --- Le haut ----------------------------------------------------------------------------------------------------
  const drawTop = () => {
    const L = clubLogo();
    const now = clock();
    const sub = g.over ? 'terminé' : paused(g) ? 'en pause' : cfg.stop15 ? '15 min au plus' : `≈ ${ABOUT_MIN[n]} min`;
    const coachLeft = coach ? Math.max(0, Math.ceil((coach.until - now) / 1000)) : 0;
    const ph = live && started ? `<button type="button" class="ph" data-phones>${ICON.phone}Téléphones · ${live.online()}</button>` : '';
    top.innerHTML = `<div class="l">${L ? `<img src="${esc(L)}" alt="">` : ''}<span><b>Le Parcours</b><small>${esc([inp.title, inp.teamLabel].filter(Boolean).join(' · '))}</small></span></div>
      <div class="c"><b data-clock>${started ? fmtGameTime(g.shared.endedAt ?? gameMs(g, now)) : '0:00'}</b><small>${esc(started ? sub : 'avant le coup d’envoi')}</small></div>
      <div class="r">${g.over ? `<button type="button" data-lib>${IC.book}Ma bibliothèque</button>`
        : `${ph}<button type="button" data-pause>${paused(g) ? `${IC.play}Reprendre` : `${IC.pause}Pause`}</button>
           <button type="button" data-coach class="${coach ? 'on' : ''}">${coach ? `${IC.flag}Arbitrage · ${coachLeft} s` : `${IC.flag}Arbitrer`}</button>`}</div>`;
  };

  // --- Les colonnes des équipes -----------------------------------------------------------------------------------
  const failShort = FAIL_LABEL[cfg.fail].short;
  const column = (id: string) => {
    const p = participants.find(x => x.id === id)!;
    const s = g.ps[id]!;
    const now = clock();
    const mine = anim?.who === id ? anim.kind : null;
    const cls = [mine ? 'glow' : '', anim?.kind === 'card' && !mine ? 'dim' : '', g.shared.winner === id ? 'won' : ''].join(' ');
    let st = '', stc = 'idle', body = '';
    const rollBtn = (dis: boolean) => `<button type="button" class="gm-btn team" data-roll="${id}" ${dis ? 'disabled' : ''}>${die(6, 34, { rot: -10 })}Lancer le dé</button>`;
    // Pendant l'animation d'une autre équipe, ses boutons restent touchables : le geste est noté et joué juste après.
    const hold = (busy && anim?.who === id) || pend.has(id) || !started;   // avant le coup d’envoi : rien ne se joue
    if (mine === 'rolling') {
      st = 'Au dé';
      body = `<button type="button" class="gm-btn rolling" disabled>${die(4, 34, { rot: 18 })}Le dé roule…</button><p class="gm-hint">Le pion avance case par case, puis la carte se retourne.</p>`;
    } else if (mine === 'card') {
      st = 'Nouvelle carte';
      body = `<div class="gm-slot">la carte va se ranger ici</div>`;
    } else if (s.phase === 'done') {
      st = 'Arrivée !'; stc = 'ok';
    } else if (s.phase === 'defi' && s.card) {
      st = `En défi <em>depuis ${fmtGameTime(gameMs(g, now) - s.since)}</em>`; stc = '';
      const sub = [...cardTags(s.card), `raté : ${failShort}`].join(' · ');
      body = askJoker === id
        ? `<p class="gm-hint">Votre joker « annule un raté » : on l’utilise ?</p><div class="gm-btns"><button type="button" class="gm-btn ok" data-jk-yes="${id}">Joker</button><button type="button" class="gm-btn no" data-jk-no="${id}">${esc(failShort)}</button></div>`
        : `${miniCard(s.card, sub)}<div class="gm-btns"><button type="button" class="gm-btn ok" data-done="${id}" ${hold || paused(g) ? 'disabled' : ''}>${IC.check}Fait !</button>
           <button type="button" class="gm-btn no" data-fail="${id}" ${hold || paused(g) ? 'disabled' : ''}>Raté</button></div>
           ${s.jokers.includes('change') ? `<button type="button" class="gm-btn joker" data-jk-change="${id}" ${hold || paused(g) ? 'disabled' : ''}>Joker : change de défi</button>` : ''}`;
    } else if (s.phase === 'bonus' && s.bonus) {
      const made = s.shots.filter(Boolean).length;
      st = `${ACTION_CASE_LABEL[s.bonus]} <em>${made} sur ${s.shots.length}</em>`; stc = '';
      const k = shotsOf(s.bonus);
      body = bonusWho() === id
        ? `<p class="gm-hint">Après le ${ord(k)} tir, ${esc(inSentence(p.name))} ${plural ? 'relancent' : 'relance'} le dé.</p>`
        : `<div class="gm-btns"><button type="button" class="gm-btn ok" data-shot="${id}" data-made="1" ${hold ? 'disabled' : ''}>Marqué</button><button type="button" class="gm-btn no" data-shot="${id}" data-made="0" ${hold ? 'disabled' : ''}>Raté</button></div>`;
    } else {
      const wait = skipping(g, id);
      st = wait ? 'Passe son tour' : s.note ?? 'Au dé'; stc = wait || (s.note && s.noteKo) ? 'ko' : s.note ? 'ok' : 'idle';
      const last = [...g.log].reverse().find(e => e.who === id && !e.undone);
      const hint = wait ? `<p class="gm-hint">${plural ? 'Elles attendent' : 'Attente'} : chaque autre ${plural ? 'équipe' : 'pion'} lance le dé une fois.</p>`
        : s.note && last ? `<p class="gm-hint">${esc(last.text.replace(/^dé \d : /, ''))}. Relancez quand vous voulez.</p>` : '';
      const jk = s.jokers.length ? `<p class="gm-hint">Joker en main : ${s.jokers.map(j => (j === 'change' ? 'change de défi' : 'annule un raté')).join(', ')}.</p>` : '';
      body = `${hint}${jk}${rollBtn(hold || !canRoll(g, id))}`;
    }
    const phone = live?.phoneOf(id) ?? null;
    const chip = phone === 'on' ? `<small class="gm-phone"><span class="sl-ph"><i></i>${ICON.phone}relié</span></small>` : phone === 'off' ? `<small class="gm-phone"><span class="sl-ph off"><i></i>${ICON.phone}hors réseau</span></small>` : '';
    return `<section class="gm-team ${cls}" data-team="${id}" style="--c:${p.color};--ci:${p.ink}">
      <div class="gm-th">${jersey(kitOf.get(id)!, 40)}<span><b>${esc(p.name)}</b>${membersLine(id) ? `<small>${esc(membersLine(id))}</small>` : ''}${chip}</span><span class="cs"><i>case</i><b>${shown.get(id) ?? s.at}</b></span></div>
      <div class="gm-st ${stc}">${st}</div><div class="gm-do">${body}</div></section>`;
  };
  const bonusWho = () => participants.find(p => g.ps[p.id]!.phase === 'bonus')?.id ?? null;

  // --- L'arbitrage ------------------------------------------------------------------------------------------------
  const coachPanel = () => {
    const who = coach!.who;
    const p = who ? participants.find(x => x.id === who)! : null;
    const ld = who ? lastDone(g, who) : null;
    const la = lastActionText(g);
    const log = recentLog(g, 5).map(e => {
      const c = e.who ? kitOf.get(e.who)?.color ?? '#9AA1AA' : '#9AA1AA';
      return `<li><time>${fmtGameTime(e.at)}</time><i style="background:${c}"></i><span>${e.who ? `<b>${esc(name(e.who))}</b> · ` : '<b>Coach</b> · '}${esc(e.text)}</span></li>`;
    }).join('');
    const sanctions = (Object.keys(SANCTION_LABEL) as Sanction[]).map(s => `<button type="button" data-sanction="${s}">${SANCTION_LABEL[s]}</button>`).join('');
    return `<section class="gm-coach" data-coachpanel>
      <div class="h">${p ? jersey(kitOf.get(p.id)!, 40) : `<span></span>`}<span><small>Arbitrer</small><b>${esc(p ? p.name : 'La partie')}</b></span><button type="button" class="x" data-coach-x aria-label="Fermer l’arbitrage">×</button></div>
      ${p ? `<button type="button" class="gm-crow main" data-invalidate ${ld ? '' : 'disabled'}><span class="ico">${IC.ban}</span><span><strong>Invalider le dernier défi</strong>
          <small>${ld ? `« ${esc(ld.title)} » : le pion revient case ${ld.backTo}, la carte aussi` : 'Aucun défi réussi à invalider'}</small></span></button>
        <div class="gm-crow plain"><span class="ico">${IC.flag}</span><span><strong>Sanction</strong><span class="chips">${sanctions}</span></span></div>` : ''}
      <button type="button" class="gm-crow" data-undo ${la ? '' : 'disabled'}><span class="ico">${IC.undo}</span><span><strong>Annuler la dernière action</strong>
        <small>${la ? `la dernière : ${esc(la.text)}, à ${fmtGameTime(la.at)}` : 'Rien à annuler'}</small></span></button>
      <button type="button" class="gm-crow" data-pauseall><span class="ico">${paused(g) ? IC.play : IC.pause}</span><span><strong>${paused(g) ? 'Reprendre' : 'Pause pour tous'}</strong>
        <small>${paused(g) ? 'le temps repart' : 'le temps s’arrête, les cartes restent'}</small></span></button>
      ${!p ? `<button type="button" class="gm-crow" data-stop><span class="ico">${IC.stop}</span><span><strong>${stopArmed ? 'Arrêter ? Touche encore' : 'Arrêter la partie'}</strong>
        <small>classement par case, puis le bilan</small></span></button>` : ''}
      <div class="gm-log"><p>Les dernières actions</p><ol>${log || '<li><span>Rien encore.</span></li>'}</ol></div></section>`;
  };
  const openCoach = (who: string | null) => {
    coach = { who, until: clock() + 30_000 };
    render();
  };
  const touchCoach = () => { if (coach) coach.until = clock() + 30_000; };

  // --- Le rendu ---------------------------------------------------------------------------------------------------
  const drawBonusPanel = () => {
    over.querySelector('.gm-bonus')?.remove();
    const id = bonusWho();
    if (!id || (anim && anim.who === id)) return;
    const s = g.ps[id]!, k = shotsOf(s.bonus!);
    const shots = Array.from({ length: k }, (_, i) => {
      if (i < s.shots.length) return s.shots[i] ? `<div class="gm-shot ok"><span class="b">${IC.check}</span><b>${ord(i + 1)} tir · marqué</b><small>+1 case</small></div>`
        : `<div class="gm-shot ko"><span class="b">✗</span><b>${ord(i + 1)} tir · raté</b><small>le pion reste</small></div>`;
      return `<div class="gm-shot ${i === s.shots.length ? 'now' : 'later'}"><span class="b">${i + 1}</span><b>${ord(i + 1)} tir</b><small>${i === s.shots.length ? 'touche après le tir' : 'ensuite'}</small></div>`;
    }).join('');
    const el = document.createElement('div');
    el.className = 'gm-bonus';
    el.innerHTML = `<div class="h"><span class="badge"><svg width="34" height="34" viewBox="-17 -17 34 34">${ACT_GLYPH[s.bonus!]}</svg></span><span><small>Case ${s.at - s.shots.filter(Boolean).length} · ${esc(name(id))}</small><b>${ACTION_CASE_LABEL[s.bonus!]}</b></span></div>
      <p class="rule">${ACTION_CASE_RULE[s.bonus!]}</p><div class="gm-shots" style="--k:${k}">${shots}</div>
      <div class="gm-btns"><button type="button" class="gm-btn ok" data-shot="${id}" data-made="1" ${busy || paused(g) ? 'disabled' : ''}>${IC.check}Marqué</button><button type="button" class="gm-btn no" data-shot="${id}" data-made="0" ${busy || paused(g) ? 'disabled' : ''}>Raté</button></div>`;
    over.append(el);
  };
  const drawPause = () => {
    over.querySelector('.gm-paused')?.remove();
    if (!paused(g) || g.over) return;
    const el = document.createElement('div');
    el.className = 'gm-paused';
    el.innerHTML = `<b>Pause</b><button type="button" class="gm-btn ok" data-resume>${IC.play}Reprendre</button>`;
    over.append(el);
  };
  let endDrawn = false;
  /** La partie publiée pour les téléphones (D-167) : rien de nominatif. */
  function publish() {
    if (!live || !started) return;
    live.publish(parcoursView(g, { seq: ++seq, t: g.shared.endedAt ?? gameMs(g, clock()) }), g.over ? 'over' : 'running');
  }
  function render() {
    publish();
    if (g.over && !busy) { if (!endDrawn) { endDrawn = true; drawEnd(); } return; }
    drawTop();
    side.className = `gm-side n${participants.length}`;
    side.style.gridAutoRows = coach ? 'auto' : '';
    side.innerHTML = coach ? coachPanel() : participants.map(p => column(p.id)).join('');
    drawBonusPanel();
    drawPause();
  }

  // --- Les effets, dans l'ordre -----------------------------------------------------------------------------------
  async function play(step: Step, sound?: () => void) {
    g = step.g;
    if (!step.fx.length) { sound?.(); placeAll(); render(); return; }
    busy = true;
    host.setAttribute('data-busy', '');
    sound?.();
    const dieFx = step.fx.find(f => f.t === 'die');
    anim = dieFx ? { who: dieFx.who, kind: 'rolling' } : null;
    render();
    let rollbox: HTMLElement | null = null;
    for (const f of step.fx) {
      if (!host.isConnected) return;
      if (f.t === 'die') rollbox = await showDie(f.n);
      else if (f.t === 'move') { await walk(f); rollbox?.remove(); rollbox = null; }
      else if (f.t === 'note') bubble(f.at, f.text, !!f.ko);
      else if (f.t === 'bonus') sndBonus();
      else if (f.t === 'card') { anim = { who: f.who, kind: 'card' }; render(); await flipCard(f.who, f.card, dieFx && dieFx.who === f.who ? dieFx.n : null); }
      else if (f.t === 'arrive') { sndFanfare(); await sleep(T(1100)); }
    }
    rollbox?.remove();
    busy = false; anim = null;
    host.removeAttribute('data-busy');
    placeAll();
    render();
    drain();
  }
  async function showDie(face: number): Promise<HTMLElement> {
    const box = document.createElement('div');
    box.className = 'gm-rollbox rolling';
    box.innerHTML = die(1 + Math.floor(Math.random() * 6), 200, { rot: -20 });
    over.append(box);
    sndRoll();
    const t0 = clock();
    while (clock() - t0 < T(820)) { await sleep(90); box.innerHTML = die(1 + Math.floor(Math.random() * 6), 200, { rot: Math.round(Math.random() * 60 - 30) }); }
    box.className = 'gm-rollbox landed';
    box.innerHTML = `${die(face, 200, { rot: -12 })}<span class="face">${face}</span>`;
    await sleep(T(650));
    return box;
  }
  async function walk(f: Extract<Fx, { t: 'move' }>) {
    if (f.from === f.to) return;
    const dir = f.to > f.from ? 1 : -1;
    // Le pion qui bouge passe devant les autres.
    const el = pawnEls.get(f.who)!;
    if (el.nextSibling) { el.parentNode!.append(el); await new Promise(r => requestAnimationFrame(() => r(null))); }
    trail(f.who, f.from, f.to);
    let k = 0;
    for (let i = f.from + dir; dir > 0 ? i <= f.to : i >= f.to; i += dir) {
      place(f.who, i);
      hop(f.who);
      sndStep(k++, dir < 0);
      // La colonne suit la case.
      const cs = side.querySelector(`[data-team="${f.who}"] .cs b`);
      if (cs) cs.textContent = String(i);
      await sleep(T(dir > 0 ? 270 : 200));
    }
    ring(f.to, kitOf.get(f.who)!.color);
    await sleep(T(200));
  }
  async function flipCard(who: string, card: Card, dieN: number | null) {
    const p = participants.find(x => x.id === who)!;
    const w = Math.min(330, Math.round((bwrap.clientHeight || 600) * .5));
    const el = document.createElement('div');
    el.className = 'gm-flip';
    el.style.setProperty('--c', p.color); el.style.setProperty('--ci', p.ink); el.style.setProperty('--fw', `${w}px`);
    el.innerHTML = `<div class="gm-dimmer"></div>
      <div class="gm-flip-head">${jersey(kitOf.get(who)!, 34)}<b>${esc(p.name)}</b><span>${dieN ? `dé ${dieN} · ` : ''}case ${g.ps[who]!.at}</span></div>
      <div class="gm-flip-stage"><div class="gm-flip-inner">${cardBack(w)}${cardHtml(card, w, { solo })}</div>
        ${SPARK(-46, 40, 30)}${SPARK(w + 22, 10, 22, '#FFFFFF')}${SPARK(w + 30, Math.round(w * .9), 34)}${SPARK(-30, Math.round(w * 1.15), 20, '#FFFFFF')}</div>
      <div class="gm-flip-foot"><span class="ring">3</span>Elle reste 3 s en grand, puis se range chez ${esc(inSentence(p.name))}.</div>`;
    // Le voile et le dos de la carte sont des calques voulus (data-lyt-ok, scripts/layout/measure.mjs).
    el.querySelectorAll('.gm-dimmer, .gm-c-back').forEach(x => x.setAttribute('data-lyt-ok', ''));
    over.append(el);
    await sleep(60);
    el.classList.add('turned');
    sndFlip();
    const ringEl = el.querySelector('.ring')!;
    for (let s = 3; s > 0; s--) { ringEl.textContent = String(s); await sleep(T(1000)); }
    el.classList.add('away');
    await sleep(T(500));
    el.remove();
  }

  // --- La fin -----------------------------------------------------------------------------------------------------
  function drawEnd() {
    coach = null;
    drawTop();
    if (!noted) {
      noted = true;
      const r = runResult(g, cfg, clock());
      void noteGameRun({ game_id: gameId, club_id: inp.clubId, team_id: inp.teamId, mode: cfg.mode, duration_s: r.duration_s, result: r.result }).catch(() => undefined);
    }
    const rk = ranking(g);
    const first = rk[0]!;
    const fp = participants.find(p => p.id === first.id)!;
    const end = g.shared.endedBy;
    const t = mmss(g.shared.endedAt ?? 0);
    const eb = end === 'arrival' ? `Arrivée · ${t}` : end === 'time' ? `Fin du temps · ${t}` : `Partie arrêtée · ${t}`;
    const h3 = end === 'arrival' ? (plural ? 'arrivent sous le panier' : 'arrive sous le panier') : (plural ? 'en tête à la fin' : 'en tête à la fin');
    const rows = rk.map(r => {
      const p = participants.find(x => x.id === r.id)!;
      return `<div class="${r.rank === 1 ? 'first' : ''}" style="--c:${p.color}"><em>${r.rank}</em>${jersey(kitOf.get(p.id)!, 40)}<span><b>${esc(p.name)}</b><span class="bar"><i style="width:${Math.round(r.at / n * 100)}%"></i></span></span><small>${r.arrived ? 'arrivée' : `case ${r.at}`}</small></div>`;
    }).join('');
    const b = bilan(g);
    const lbl = solo ? 'Joueur' : 'Équipe';
    const tr = b.rows.map(r => { const p = participants.find(x => x.id === r.id)!; return `<tr><td><span><i style="background:${p.color}"></i>${esc(p.name.replace(/^Les /, ''))}</span></td><td class="ok">${r.ok}</td><td class="ko">${r.ko}</td><td>${r.avgMs !== null ? fmtGameTime(r.avgMs) : '—'}</td></tr>`; }).join('');
    const miss = b.missed.map(m => `<li style="--fam:${FAM_HEX[m.card.fam ?? 'tir']}"><i>${famPicto(m.card.fam ?? 'tir', 17)}</i><span>${esc([cardTitle(m.card), ...cardTags(m.card).filter(x => /main|côté/.test(x))].join(', '))}</span><b>${m.ko} sur ${m.tries}</b></li>`).join('');
    const ins = b.insight;
    const ART: Record<string, string> = { 'main gauche': 'La main gauche', 'main droite': 'La main droite', 'côté gauche': 'Le côté gauche', 'côté droit': 'Le côté droit', 'des deux côtés': 'Les deux côtés', tir: 'Le tir', dribble: 'Le dribble', passe: 'La passe', physique: 'Le physique' };
    const learn = ins ? `<div class="gm-learn"><b>${esc(ART[ins.label] ?? `« ${ins.label} »`)} coince</b> : ${ins.ko} ratés sur ${ins.tries} défis ${ins.tag ? `« ${esc(ins.label)} »` : `de ${esc(ins.label)}`}${solo ? '' : ', toutes équipes'}. Une idée pour la prochaine séance.</div>` : '';
    const colors = participants.map(p => p.color);
    main.style.gridTemplateColumns = '';
    main.innerHTML = `<div class="gm-win" data-win>${confetti(colors)}
        <p class="eb">${esc(eb)}</p>${jersey(kitOf.get(fp.id)!, 150, 'big')}
        <h2>${esc(fp.name)}</h2><h3>${h3}</h3>
        <div class="gm-rank">${rows}</div>
        <div class="acts"><button type="button" class="p" data-again>Rejouer</button>${gameId ? '' : `<button type="button" class="g" data-keep>Garder ce jeu</button>`}<button type="button" class="g" data-close>Fermer</button></div></div>
      <section class="gm-bilan"><p class="eb">Pour le coach</p><h3>Ce que la partie dit</h3>
        <table><tr><th>${lbl}</th><th>Défis réussis</th><th>ratés</th><th>par défi</th></tr>${tr}</table>
        ${miss ? `<p class="eb" style="color:#737A83">Les cartes les plus ratées</p><ul class="gm-miss">${miss}</ul>` : ''}
        ${learn}
        <p class="gm-kept">${solo ? 'Gardé : la partie et sa durée. Rien par personne, aucun classement individuel.' : 'Gardé : la partie, sa durée, le résultat par équipe. Aucun prénom, aucun classement individuel.'}</p></section>`;
    main.classList.add('gm-end');
    main.style.gridTemplateColumns = 'minmax(0, 1fr) 470px';
    sndFanfare();
  }

  // --- Les gestes -------------------------------------------------------------------------------------------------
  const rnd = () => Math.random();
  /** Les gestes des téléphones (D-167), dans l'ordre de la base, un par un quand rien ne bouge à l'écran. */
  function drain() {
    while (queue.length && !busy && !g.over) {
      const a = queue.shift()!;
      const now = clock();
      const p = a.payload;
      const seat = a.seat === 'coach' ? (typeof p.seat === 'string' ? p.seat : null) : a.seat;
      if (a.id === 0 && seat) pend.delete(seat);
      const before = g;
      if (seat && !g.ps[seat]) continue;
      if (a.kind === 'roll' && seat) void play(roll(g, seat, rnd, now));
      else if (a.kind === 'done' && seat) void play(declare(g, seat, true, rnd, now), sndOk);
      else if (a.kind === 'fail' && seat) void play(declare(g, seat, false, rnd, now, { joker: p.joker === 'cancel' }), p.joker === 'cancel' ? sndOk : sndKo);
      else if (a.kind === 'shot' && seat) void play(shot(g, seat, p.made === true, now), p.made === true ? sndOk : sndKo);
      else if (a.kind === 'joker' && seat && p.joker === 'change') void play(jokerChange(g, seat, rnd, now));
      else if (a.kind === 'invalidate' && seat) void play(invalidate(g, seat, now), sndKo);
      else if (a.kind === 'sanction' && seat && ['back1', 'back2', 'back3', 'skip'].includes(String(p.value))) void play(sanction(g, seat, p.value as Sanction, now), sndKo);
      else if (a.kind === 'pause') { g = pause(g, p.on === undefined ? !paused(g) : p.on === true, now); render(); }
      else if (a.kind === 'undo') void play(undo(g, now));
      else if (a.kind === 'end') { g = endGame(g, 'coach', now); render(); }
      else if (a.kind === 'qr') openJoin(true);
      if (a.seat === 'coach' && g !== before) flash(host, `${['roll', 'done', 'fail', 'shot', 'joker'].includes(a.kind) ? 'Télécommande de' : 'Arbitré par'} ${live?.coachName() ?? 'le coach'}${seat ? ` · ${nameOfP(g, seat)}` : ''}`);
    }
  }
  host.addEventListener('click', e => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('button');
    if (!b || (b as HTMLButtonElement).disabled) return;
    const now = clock();
    const d = b.dataset;
    if (b.hasAttribute('data-phones') && live) { openJoin(true); return; }
    if (b.hasAttribute('data-join-back')) { closeJoin(); return; }
    if (b.hasAttribute('data-join-none')) { const l = live; live = null; closeJoin(); void l?.close(); void kickoff(); return; }
    if (b.hasAttribute('data-join-go')) { closeJoin(); void kickoff(); return; }
    // Une équipe touche pendant l'animation d'une autre : noté, joué juste après (D-174 : un seul appareil pour toutes).
    const later = (kind: LiveAction['kind'], seat: string, payload: Record<string, unknown> = {}) => {
      if (!busy) return false;
      if (anim?.who !== seat && !pend.has(seat)) { pend.add(seat); queue.push({ id: 0, seat, kind, payload, at: now }); render(); }
      return true;
    };
    if (d.roll) { if (!later('roll', d.roll)) void play(roll(g, d.roll, rnd, now)); }
    else if (d.done) { if (!later('done', d.done)) void play(declare(g, d.done, true, rnd, now), sndOk); }
    else if (d.fail) {
      if (g.ps[d.fail]!.jokers.includes('cancel')) { askJoker = d.fail; render(); return; }
      if (!later('fail', d.fail)) void play(declare(g, d.fail, false, rnd, now), sndKo);
    } else if (d.jkYes) { askJoker = null; if (!later('fail', d.jkYes, { joker: 'cancel' })) void play(declare(g, d.jkYes, false, rnd, now, { joker: true }), sndOk); }
    else if (d.jkNo) { askJoker = null; if (!later('fail', d.jkNo)) void play(declare(g, d.jkNo, false, rnd, now), sndKo); }
    else if (d.jkChange) { if (!later('joker', d.jkChange, { joker: 'change' })) void play(jokerChange(g, d.jkChange, rnd, now)); }
    else if (d.shot) { if (!later('shot', d.shot, { made: d.made === '1' })) void play(shot(g, d.shot, d.made === '1', now), d.made === '1' ? sndOk : sndKo); }
    else if (b.hasAttribute('data-pause') || b.hasAttribute('data-resume')) { g = pause(g, !paused(g), now); render(); }
    else if (b.hasAttribute('data-coach')) { if (coach) { coach = null; stopArmed = false; render(); } else openCoach(null); }
    else if (b.hasAttribute('data-coach-x')) { coach = null; stopArmed = false; render(); }
    else if (b.hasAttribute('data-invalidate') && coach?.who) { touchCoach(); void play(invalidate(g, coach.who, now), sndKo); }
    else if (d.sanction && coach?.who) { touchCoach(); void play(sanction(g, coach.who, d.sanction as Sanction, now), sndKo); }
    else if (b.hasAttribute('data-undo')) { touchCoach(); void play(undo(g, now)); }
    else if (b.hasAttribute('data-pauseall')) { touchCoach(); g = pause(g, !paused(g), now); render(); }
    else if (b.hasAttribute('data-stop')) {
      touchCoach();
      if (!stopArmed) { stopArmed = true; render(); setTimeout(() => { stopArmed = false; if (coach && host.isConnected) render(); }, 4000); return; }
      stopArmed = false; g = endGame(g, 'coach', now); render();
    }
    else if (b.hasAttribute('data-again')) restart();
    else if (b.hasAttribute('data-keep')) void (async () => { b.setAttribute('disabled', ''); b.textContent = 'Gardé…'; gameId = (await inp.keep?.().catch(() => null)) ?? gameId; b.textContent = gameId ? 'Gardé ✓' : 'Impossible'; })();
    else if (b.hasAttribute('data-close') || b.hasAttribute('data-lib')) close(b.hasAttribute('data-lib'));
  });

  // L'appui long de 1,5 s sur la colonne (ou la ligne) d'une équipe : son arbitrage, tout de suite (sans code, D-174).
  let press: { t: number; id: string; x: number; y: number } | null = null;
  const cancelPress = () => { if (press) { clearTimeout(press.t); side.querySelector('.pressing')?.classList.remove('pressing'); press = null; } };
  host.addEventListener('pointerdown', e => {
    const col = (e.target as HTMLElement).closest<HTMLElement>('.gm-team');
    if (!col || g.over || (e.target as HTMLElement).closest('button')) return;
    cancelPress();
    col.classList.add('pressing');
    const id = col.dataset.team!;
    press = { id, x: e.clientX, y: e.clientY, t: window.setTimeout(() => { cancelPress(); openCoach(id); }, 1500) };
  });
  host.addEventListener('pointermove', e => { if (press && Math.hypot(e.clientX - press.x, e.clientY - press.y) > 12) cancelPress(); });
  for (const ev of ['pointerup', 'pointercancel', 'pointerleave']) host.addEventListener(ev, cancelPress);
  host.addEventListener('contextmenu', e => e.preventDefault());

  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { if (g.over) close(); else if (coach) { coach = null; stopArmed = false; render(); } else openCoach(null); } };
  document.addEventListener('keydown', onKey);

  // L'horloge : le temps de jeu, l'arbitrage qui se referme, « Arrêter à 15 min ».
  const timer = window.setInterval(() => {
    if (!host.isConnected) return;
    const now = clock();
    if (started && !g.over && cfg.stop15 && !busy && gameMs(g, now) >= STOP_MS) { g = endGame(g, 'time', now); render(); return; }
    if (coach && now > coach.until) { coach = null; stopArmed = false; render(); return; }
    if (g.over) return;
    const clockEl = top.querySelector('[data-clock]');
    if (clockEl && started) clockEl.textContent = fmtGameTime(gameMs(g, now));
    const cb = top.querySelector('[data-coach]');
    if (coach && cb) cb.innerHTML = `${IC.flag}Arbitrage · ${Math.max(0, Math.ceil((coach.until - now) / 1000))} s`;
    for (const p of participants) {
      const s = g.ps[p.id]!;
      if (s.phase !== 'defi' || coach || anim?.who === p.id) continue;
      const em = side.querySelector(`[data-team="${p.id}"] .gm-st em`);
      if (em) em.textContent = `depuis ${fmtGameTime(gameMs(g, now) - s.since)}`;
    }
  }, 250);

  function restart() {
    queue.length = 0; pend.clear();
    g = newParcours(cfg, participants, Math.random, clock());
    noted = false; endDrawn = false; coach = null; askJoker = null;
    main.classList.remove('gm-end');
    main.style.gridTemplateColumns = '';
    main.innerHTML = `<div class="gm-bwrap">${boardSvg({ n, actions: cfg.actions })}<div class="gm-over"></div></div><div class="gm-side n${participants.length}"></div>`;
    side = main.querySelector<HTMLElement>('.gm-side')!;
    bwrap = main.querySelector<HTMLElement>('.gm-bwrap')!;
    over = main.querySelector<HTMLElement>('.gm-over')!;
    svg = main.querySelector<SVGSVGElement>('.gm-board')!;
    buildPawns();
    placeAll(true);
    render();
  }
  function close(toLibrary = false) {
    clearInterval(timer);
    void live?.close();
    document.removeEventListener('keydown', onKey);
    release();
    host.remove();
    inp.onClose?.();
    if (toLibrary && inp.teamId) location.hash = `#/basket/drills/${inp.teamId}`;
  }

  // --- Les téléphones (D-167) : « Rejoindre avec un téléphone », puis le coup d'envoi ---------------------------------
  const seats: LiveTeamSeat[] = participants.map((p, i) => ({ seat: p.id, bib: cfg.teams[i]?.bib ?? 'red', kit: kitOf.get(p.id)!, name: p.name }));
  let joinBox: HTMLElement | null = null;
  let joinDraw: (() => void) | null = null;
  function openJoin(reopen: boolean) {
    if (!live) return;
    closeJoin();
    joinBox = document.createElement('div');
    joinBox.className = 'sl-joinbox';
    host.append(joinBox);
    joinDraw = drawJoin(joinBox, live, { teams: seats, reopen, phones: cfg.phones, remote: cfg.remote });
  }
  function closeJoin() { joinBox?.remove(); joinBox = null; joinDraw = null; }
  async function kickoff() {
    await countdown(host, reduce);
    if (!host.isConnected) return;
    started = true;
    restart();
  }

  buildPawns();
  placeAll(true);
  render();
  if (!started) {
    void (async () => {
      live = await openLive({
        teamId: inp.teamId!, gameId, kind: 'parcours', title: inp.title, teams: seats,
        onAction: a => { queue.push(a); drain(); },
        onSeats: () => { joinDraw?.(); if (started && !joinBox) { const ph = top.querySelector('[data-phones]'); if (ph) ph.innerHTML = `${ICON.phone}Téléphones · ${live?.online() ?? 0}`; for (const p of participants) { if (!busy && !coach) { render(); break; } } } },
        onToast: text => flash(host, text),
      });
      if (!host.isConnected) { void live?.close(); return; }
      if (!live) { flash(host, 'Les téléphones ne se relient pas : on joue sur la tablette.'); void kickoff(); return; }
      openJoin(false);
    })();
  }
}
